import { yupResolver } from '@hookform/resolvers/yup';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import ImageUploadControl from 'components/formfields/ImageUploadControl';

import { VideoFunction, VideoFunctionListItem } from 'features/VideoFunction/models/videoFunction';
import { useEffect, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import VideoUploadControl from 'components/formfields/VideoUploadControl';
import { selectIsEditMode } from 'features/common/editModeSlice';
import videoFunctionService from 'features/VideoFunction/videoFunctionService';
import videoFunctionListService from 'features/VideoFunction/videoFunctionServiceList';
import { displayError, handleServiceError } from 'utils/axiosError';
import { IMAGE_SIZES } from 'utils/constants';
import * as yup from 'yup';
import './style.scss';

export interface VideoFunctionDetailModalProps {
  videoFunctionId?: number;
  isShowModal: boolean;
  videoChangedData?: VideoFunction[];
  onClose?: (isDataChanged: boolean, dataChanged?: VideoFunctionListItem) => void;
  onUnSelectAll?: () => void;
  onReloadData?: () => void;
}

function VideoFunctionDetailModal({
  videoFunctionId,
  videoChangedData,
  isShowModal,
  onClose,
  onUnSelectAll,
  onReloadData,
}: VideoFunctionDetailModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  //state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [videoFunctionData, setVideoFunctionData] = useState<VideoFunction>();
  const [isLoading, setIsLoading] = useState(true);
  // ref

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // yup setup
  const schema = yup.object({
    name: yup.string().required(t('videoFunctionDetailModal.warn_field_required')),
    thumbnail_img: yup.string().required(t('videoFunctionDetailModal.warn_field_required')),
    video: yup.string().required(t('videoFunctionDetailModal.warn_field_required')),
  });

  // form setup
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<Partial<VideoFunction>>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      priority: null,
      display_name_ja: '',
      display_name_en: '',
      display_name_vi: '',
      display_name_ko: '',
      display_name_zh_hant: '',
      display_name_zh_hans: '',
      thumbnail_img: '',
      video: '',
      note: '',
    },
    mode: 'onSubmit',
  });

  // load data from id (edit form)
  useEffect(() => {
    const fetchVideoFunctionData = async (listDataChanged?: VideoFunction[]) => {
      if (!videoFunctionId) {
        setIsLoading(false); // Handle when no ID (add mode)
        return;
      }
      try {
        const videoFunction =
          listDataChanged?.find((item) => item.id === videoFunctionId) ||
          (await videoFunctionService.getItem(videoFunctionId)).data;

        setVideoFunctionData(videoFunction);
        reset(videoFunction);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchVideoFunctionData(videoChangedData && Object.keys(videoChangedData).length > 0 ? videoChangedData : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoFunctionId, reset, videoChangedData]);

  useEffect(() => {
    register('thumbnail_img', {
      validate: (value) => !!value || t('videoFunctionDetailModal.warn_field_required'),
    });
  }, [register, t]);

  if (isLoading) {
    return null; // Optional loading indicator
  }

  function onSubmit(data: Partial<VideoFunction>) {
    if (videoFunctionId) {
      //update data
      (async () => {
        try {
          await videoFunctionListService.updates([{ ...data, id: videoFunctionId } as VideoFunction]);
          showGlobalModal(() => (
            <AlertModal
              title={t('App.info')}
              message={t('videoFunction.sucesss_save')}
              onClose={handleClose.bind(null, true, data as VideoFunctionListItem)}
            />
          ));
          handleUnSelectAll();
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    } else {
      // insert data

      (async () => {
        try {
          const response = await videoFunctionListService.add(data);
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
          ));
          handleUnSelectAll();
          handleReloadData();
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  function handleClose(isDataChanged: boolean = false, dataChanged?: VideoFunctionListItem) {
    onClose && onClose(isDataChanged, dataChanged);
  }
  function handleUnSelectAll() {
    onUnSelectAll && onUnSelectAll();
  }
  function handleReloadData() {
    onReloadData && onReloadData();
  }

  return (
    <Modal
      show={isShowModal}
      dialogClassName="videofunction-detail-modal"
      backdrop="static"
      onHide={handleClose}
      centered
    >
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('videoFunctionDetailModal.title')} />
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex justify-content-end my-3">
            <div className="ps-3">
              <Button type="submit" className="btn btn--dark-green">
                {t('videoFunctionDetailModal.button.save')}
              </Button>
            </div>
          </div>

          <Table bordered className="form-video-function">
            <tbody>
              <tr>
                <th colSpan={3} style={{ width: '40%' }}>
                  {t('videoFunctionDetailModal.video_name')}
                </th>
                <td colSpan={3} style={{ width: '60%' }}>
                  <Form.Control className="text-center" {...register('name')}></Form.Control>
                  {errors.name && <small className="text-danger d-inline-block mt-1">{errors.name.message}</small>}
                </td>
              </tr>

              <tr>
                <th rowSpan={6} colSpan={2} style={{ width: '20%' }}>
                  {t('videoFunctionDetailModal.display_name.title')}
                </th>
                <th style={{ width: '20%' }}>{t('videoFunctionDetailModal.display_name.jp')}</th>
                <td colSpan={3} style={{ width: '60%' }}>
                  <Form.Control {...register('display_name_ja')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('videoFunctionDetailModal.display_name.en')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_en')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('videoFunctionDetailModal.display_name.cnt')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_zh_hant')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('videoFunctionDetailModal.display_name.cns')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_zh_hans')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('videoFunctionDetailModal.display_name.ko')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_ko')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('videoFunctionDetailModal.display_name.vi')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_vi')}></Form.Control>
                </td>
              </tr>
              {/* Thumnail image */}
              <tr className="">
                <th colSpan={3}>{t('videoFunctionDetailModal.thumbnail_img')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    // {...register('thumbnail_img')}
                    name="thumbnail_img"
                    control={control}
                    minWidth={IMAGE_SIZES.video_function_video_thumbnail.minWidth}
                    minHeight={IMAGE_SIZES.video_function_video_thumbnail.minHeight}
                    maxWidth={IMAGE_SIZES.video_function_video_thumbnail.maxWidth}
                    maxHeight={IMAGE_SIZES.video_function_video_thumbnail.maxHeight}
                    maxFileSize={IMAGE_SIZES.video_function_video_thumbnail.maxFileSize}
                  />
                  {errors.thumbnail_img && (
                    <div className="text-danger mt-2 text-end">{errors.thumbnail_img.message}</div>
                  )}
                </td>
              </tr>
              {/* Video */}
              <tr className={''}>
                <th colSpan={3}>{t('videoFunctionDetailModal.video')}</th>
                <td colSpan={3}>
                  <VideoUploadControl
                    //{...register('video')}
                    name="video"
                    control={control}
                    maxFileSize={0}
                  />
                  {errors.video && <div className="text-danger mt-2 text-end">{errors.video.message}</div>}
                </td>
              </tr>
              <tr>
                <th colSpan={3}>{t('videoFunctionDetailModal.note')}</th>
                <td colSpan={3}>
                  <Form.Control as="textarea" rows={3} {...register('note')}></Form.Control>
                </td>
              </tr>
            </tbody>
          </Table>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default VideoFunctionDetailModal;
